import React from "react";
import styled from "styled-components";
import { Tag } from "../../../../../design-system";
import type { OperationResult, OpError } from "../../../../../database/mediaOps/mediaOpsApi";
import { deliveryChip, publicationChip, scheduleChip } from "./stateLabels";

// Dense operator primitives for the Delivery section. Design-system tokens
// only. Mobile: controls are at least 40px tall below 600px.

export const Panel = styled.section`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  border-radius: var(--radius-xl);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  box-shadow: var(--shadow-xs);
  min-width: 0;
`;

export const PanelHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  font-family: var(--font-display);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

export const Hint = styled.p`
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  line-height: 1.5;
  color: var(--color-text-secondary);
`;

export const Small = styled.span`
  font-family: var(--font-sans);
  font-size: 12px;
  color: var(--color-text-secondary);
`;

export const Mono = styled.code`
  font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
  font-size: 12px;
  color: var(--color-text-secondary);
  word-break: break-all;
`;

export const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
`;

export const Grow = styled.span`
  flex: 1 1 auto;
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--space-3);
`;

export const FieldLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  min-width: 0;
`;

export const CheckLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  line-height: 1.4;
  cursor: pointer;

  input {
    margin-top: 3px;
  }
`;

const controlCss = `
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 7px 10px;
  min-height: 34px;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  @media (max-width: 600px) {
    min-height: 44px;
    font-size: 16px;
  }
`;

export const TextInput = styled.input`
  ${controlCss}
`;

export const TextArea = styled.textarea`
  ${controlCss}
  resize: vertical;
  min-height: 80px;
  line-height: 1.45;
`;

export const Select = styled.select`
  ${controlCss}
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: var(--font-sans);
  font-size: 12px;
  color: var(--color-text);

  th {
    text-align: left;
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 10px;
    padding: 6px 8px;
    border-bottom: 1px solid var(--color-border-subtle);
  }
  td {
    padding: 6px 8px;
    border-bottom: 1px solid var(--color-border-subtle);
    vertical-align: top;
  }
`;

export const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
`;

export const ListRow = styled.div<{ $active?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  padding: 10px 12px;
  border-radius: var(--radius-md);
  border: 1px solid ${(p) => (p.$active ? "var(--color-accent)" : "var(--color-border-subtle)")};
  background: ${(p) => (p.$active ? "var(--color-accent-subtle)" : "var(--color-surface)")};
`;

export const Banner = styled.div<{ $tone: "danger" | "warning" | "info" | "success" }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  line-height: 1.45;
  color: var(--color-text);
  background: ${(p) => `var(--color-${p.$tone}-subtle)`};
  border: 1px solid ${(p) => `var(--color-${p.$tone})`};
`;

export const Thumb = styled.img`
  width: 64px;
  height: 80px;
  object-fit: cover;
  border-radius: 6px;
  background: var(--color-surface-subtle);
  flex-shrink: 0;
`;

export const ThumbBox = styled.div`
  width: 64px;
  height: 80px;
  border-radius: 6px;
  background: var(--color-surface-subtle);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-sans);
  font-size: 10px;
  color: var(--color-text-tertiary);
  text-align: center;
  flex-shrink: 0;
`;

const actionText = (a?: string) => (a ? a.replace(/_/g, " ") : "");

export const ErrorLine: React.FC<{ error: OpError }> = ({ error }) => (
  <div>
    <strong>{error.code}</strong>
    {error.accountId ? <Small> (account {error.accountId})</Small> : null}
    {error.slotId ? <Small> (slot {error.slotId})</Small> : null}: {error.message}
    {error.requiredAction ? <Small> Next step: {actionText(error.requiredAction)}.</Small> : null}
    {error.retryable ? <Small> You can retry.</Small> : null}
  </div>
);

/** Renders an envelope honestly: status, every error and every warning. */
export const ResultNotice: React.FC<{ result: OperationResult | null; okText?: string }> = ({ result, okText }) => {
  if (!result) return null;
  const forbidden = result.errors.some((e) => e.code === "FORBIDDEN");
  const tone =
    result.status === "blocked" ? "danger" : result.status === "partial" ? "warning" : result.status === "queued" ? "info" : "success";
  const head =
    forbidden
      ? "Forbidden. You do not have permission for this action."
      : result.status === "blocked"
      ? "Blocked. Nothing was changed for the blocked items."
      : result.status === "partial"
      ? "Partly done. Some items were blocked."
      : result.status === "queued"
      ? "Queued. The worker will pick it up."
      : okText || "Done.";
  return (
    <Banner $tone={tone} role={result.status === "blocked" ? "alert" : "status"}>
      <strong>{head}</strong>
      {result.errors.map((e, i) => (
        <ErrorLine key={`e${i}`} error={e} />
      ))}
      {result.warnings.map((w, i) => (
        <div key={`w${i}`}>
          <strong>{w.code}</strong>: {w.message}
        </div>
      ))}
    </Banner>
  );
};

export const StateChips: React.FC<{
  states: { schedule: string; delivery: string; publication: string };
  mode: string;
  /** Stored publication evidence source (owner, admin, mcp, script). */
  publicationSource?: string | null;
}> = ({ states, mode, publicationSource }) => {
  const s = scheduleChip(states.schedule);
  const d = deliveryChip(states.delivery, mode);
  const p = publicationChip(states.publication, publicationSource);
  return (
    <Row data-testid="state-chips">
      <Tag size="sm" variant={s.variant} title="Schedule">
        Schedule: {s.label}
      </Tag>
      <Tag size="sm" variant={d.variant} title="Delivery">
        Delivery: {d.label}
      </Tag>
      <Tag size="sm" variant={p.variant} title="Publication">
        Publication: {p.label}
      </Tag>
    </Row>
  );
};

export const fmtTime = (iso?: string | null): string => {
  if (!iso) return "";
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? new Date(ms).toLocaleString() : iso;
};

export const usd = (n?: number | null): string => (typeof n === "number" ? `$${n.toFixed(2)}` : "unknown");
